import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { toast } from 'react-toastify';
import DocumentPrintOptionsModal from '../components/DocumentPrintOptionsModal';

const SaleInvoiceForm = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isNew = !id;
    
    const [loading, setLoading] = useState(false);
    const [printOptions, setPrintOptions] = useState({ open: false, context: null });
    const [showCustomerSearch, setShowCustomerSearch] = useState(false);
    const [showArticleSearch, setShowArticleSearch] = useState(false);
    const [selectedLineIndex, setSelectedLineIndex] = useState(null);
    const [customers, setCustomers] = useState([]);
    const [articles, setArticles] = useState([]);
    const [warehouses, setWarehouses] = useState([]);
    const trackingResolverRef = useRef(null);
    const cellRefs = useRef({});
    const [customerSearchTerm, setCustomerSearchTerm] = useState('');
    const [articleSearchTerm, setArticleSearchTerm] = useState('');
    const [showSuspendedModal, setShowSuspendedModal] = useState(false);
    const [suspendedDocs, setSuspendedDocs] = useState([]);
    const [loadingSuspended, setLoadingSuspended] = useState(false);
    const [sendingSms, setSendingSms] = useState(false);
    const [showArchiveModal, setShowArchiveModal] = useState(false);
    const [archiveFiles, setArchiveFiles] = useState([]);
    const [loadingArchive, setLoadingArchive] = useState(false);
    const [uploadingArchive, setUploadingArchive] = useState(false);
    const [trackingModal, setTrackingModal] = useState({
        open: false,
        lineName: '',
        requiredQty: 0,
        lotStrategy: 'FIFO',
        lotEnabled: false,
        serialEnabled: false,
        lots: [],
        serials: []
    });
    const archiveFileInputRef = useRef(null);
    const [invoice, setInvoice] = useState({
        documentType: 'Encomenda Cliente',
        series: 'Série 2',
        invoiceNumber: isNew ? '' : '',
        invoiceDate: new Date().toISOString().split('T')[0],
        customerCode: '',
        customerId: null,
        customerName: '',
        taxZone: 'Continente Portugal',
        paymentTerms: 'Não usar',
        paymentMethod: 'Nenhum',
        warehouseId: 1,
        dueDate: '',
        stockDate: new Date().toISOString(),
        reference: '',
        address: '',
        postalCode: '',
        taxNumber: '',
        balance: 0,
        notes: '',
        lines: [],
        subTotal: 0,
        taxTotal: 0,
        lineDiscount: 0,
        globalDiscount: 0,
        total: 0
    });

    const mapDocumentTypeToLabel = (code) => {
        if (code === 'FT') return 'Fatura';
        if (code === 'FR') return 'Fatura-Recibo';
        if (code === 'OR') return 'Encomenda Cliente';
        if (code === 'ORC') return 'Orçamento';
        return 'Encomenda Cliente';
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: 'EUR' }).format(value || 0);
    };
    const getCustomerCode = (customer) => customer?.codErp || customer?.reference || customer?.id?.toString() || '-';

    useEffect(() => {
        if (!isNew) {
            loadInvoice();
        }
        loadWarehouses();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const loadWarehouses = async () => {
        try {
            const res = await api.get('/warehouse?active=true');
            const list = Array.isArray(res.data) ? res.data : [];
            setWarehouses(list);
            // Define default se ainda não houver armazém escolhido
            const def = list.find(w => w.isDefault) || list[0];
            if (def && (!invoice.warehouseId || invoice.warehouseId === 1)) {
                setInvoice(prev => ({ ...prev, warehouseId: def.id }));
            }
        } catch { /* sem armazéns disponíveis */ }
    };

    const loadInvoice = async () => {
        try {
            setLoading(true);
            const res = await api.get(`/billing/sales/invoices/${id}`);
            const data = res.data || {};
            setInvoice({
                ...invoice,
                ...data,
                documentType: mapDocumentTypeToLabel(data.documentType),
                customerId: data.userId || null,
                customerCode: data.userId ? String(data.userId) : '',
                customerName: data.clientName || data.user?.name || '',
                address: data.clientAddress || '',
                postalCode: data.clientPostalCode || '',
                taxNumber: data.clientNIF || '',
                invoiceDate: data.issueDate?.split('T')[0] || '',
                dueDate: data.dueDate?.split('T')[0] || '',
                lines: Array.isArray(data.lines) ? data.lines.map(line => ({
                    articleId: line.articleId || null,
                    reference: line.reference || line.article?.reference || '',
                    description: line.description || '',
                    quantity: Number(line.quantity) || 1,
                    availableQty: Number(line.article?.stock) || 0,
                    unitPrice: Number(line.unitPrice) || 0,
                    discountPercent: Number(line.discountPercent) || 0,
                    vatPercent: Number(line.vatPercent) || 23,
                    lineTotal: Number(line.total) || 0
                })) : []
            });
        } catch (error) {
            toast.error('Erro ao carregar fatura');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const searchCustomer = async () => {
        try {
            const res = await api.get(`/users?search=${customerSearchTerm || ''}`);
            setCustomers(res.data || []);
            setShowCustomerSearch(true);
        } catch (error) {
            toast.error('Erro ao buscar clientes');
            console.error(error);
        }
    };

    const selectCustomer = (customer) => {
        setInvoice({
            ...invoice,
            customerId: customer.id,
            customerCode: getCustomerCode(customer),
            customerName: customer.name,
            address: customer.morada || customer.address || '',
            postalCode: customer.codPostal || customer.postalCode || '',
            taxNumber: customer.nif || customer.taxNumber || ''
        });
        setShowCustomerSearch(false);
        toast.success(`Cliente selecionado: ${customer.name}`);
    };

    const setCellRef = (lineIndex, field) => (el) => {
        if (!el) return;
        cellRefs.current[`${lineIndex}-${field}`] = el;
    };

    const focusCell = (lineIndex, field) => {
        setTimeout(() => {
            const el = cellRefs.current[`${lineIndex}-${field}`];
            if (el && typeof el.focus === 'function') el.focus();
            if (el && typeof el.select === 'function') el.select();
        }, 0);
    };

    const searchArticles = async (lineIndex, termOverride = null) => {
        try {
            const term = termOverride != null ? termOverride : (articleSearchTerm || '');
            const res = await api.get(`/billing/articles?search=${encodeURIComponent(term)}`);
            setArticles(res.data || []);
            setSelectedLineIndex(lineIndex);
            setShowArticleSearch(true);
        } catch (error) {
            toast.error('Erro ao buscar artigos');
            console.error(error);
        }
    };

    const applyArticleToLine = (lineIndex, article) => {
        const newLines = [...invoice.lines];
        newLines[lineIndex] = {
            ...newLines[lineIndex],
            articleId: article.id,
            reference: article.reference,
            description: article.description,
            unitPrice: article.salePriceWithVat || article.salePriceNoVat || 0,
            vatPercent: article.vatPercent || 23,
            availableQty: article.stock || 0,
            lotControl: !!article.lotControl,
            lotStrategy: article.lotStrategy || 'FIFO',
            serialNumberControl: !!article.serialNumberControl
        };

        const line = newLines[lineIndex];
        const subtotal = line.quantity * line.unitPrice;
        const discount = subtotal * (line.discountPercent / 100);
        const afterDiscount = subtotal - discount;
        const tax = afterDiscount * (line.vatPercent / 100);
        line.lineTotal = afterDiscount + tax;

        setInvoice({ ...invoice, lines: newLines });
        calculateTotals(newLines);
    };

    const selectArticle = (article) => {
        if (selectedLineIndex !== null) {
            applyArticleToLine(selectedLineIndex, article);
            setShowArticleSearch(false);
            setSelectedLineIndex(null);
            toast.success(`Artigo selecionado: ${article.description}`);
            focusCell(selectedLineIndex, 'description');
        }
    };

    const resolveReferenceOnEnter = async (lineIndex) => {
        const raw = (invoice.lines?.[lineIndex]?.reference || '').toString().trim();
        if (!raw) {
            await searchArticles(lineIndex, '');
            return { matched: false };
        }

        try {
            const res = await api.get(`/billing/articles?search=${encodeURIComponent(raw)}`);
            const list = Array.isArray(res.data) ? res.data : [];
            setArticles(list);

            const match = list.find(a => (a?.reference || '').toString().trim().toLowerCase() === raw.toLowerCase());
            if (match) {
                applyArticleToLine(lineIndex, match);
                return { matched: true };
            }

            setArticleSearchTerm(raw);
            setSelectedLineIndex(lineIndex);
            setShowArticleSearch(true);
            toast.warning('Código não encontrado. Pesquise na lista.');
            return { matched: false };
        } catch (e) {
            console.error(e);
            await searchArticles(lineIndex, raw);
            return { matched: false };
        }
    };

    const handleLineKeyDown = async (e, lineIndex, field) => {
        if (e.key !== 'Enter') return;
        e.preventDefault();

        if (field === 'reference') {
            const r = await resolveReferenceOnEnter(lineIndex);
            if (r?.matched) focusCell(lineIndex, 'description');
            return;
        }

        if (field === 'description') { focusCell(lineIndex, 'quantity'); return; }
        if (field === 'quantity') { focusCell(lineIndex, 'unitPrice'); return; }
        if (field === 'unitPrice') { focusCell(lineIndex, 'discountPercent'); return; }
        if (field === 'discountPercent') {
            const newIndex = invoice.lines.length;
            addLine();
            focusCell(newIndex, 'reference');
            return;
        }
    };

    const addLine = () => {
        setInvoice({
            ...invoice,
            lines: [...invoice.lines, {
                reference: '',
                articleId: null,
                description: '',
                quantity: 1,
                availableQty: 0,
                unitPrice: 0,
                discountPercent: 0,
                vatPercent: 23,
                lineTotal: 0
            }]
        });
    };

    const removeLine = (index) => {
        const newLines = invoice.lines.filter((_, i) => i !== index);
        setInvoice({ ...invoice, lines: newLines });
        calculateTotals(newLines);
    };

    const updateLine = (index, field, value) => {
        const newLines = [...invoice.lines];
        newLines[index][field] = value;
        
        const line = newLines[index];
        const subtotal = line.quantity * line.unitPrice;
        const discount = subtotal * (line.discountPercent / 100);
        const afterDiscount = subtotal - discount;
        const tax = afterDiscount * (line.vatPercent / 100);
        line.lineTotal = afterDiscount + tax;
        
        setInvoice({ ...invoice, lines: newLines });
        calculateTotals(newLines);
    };

    const handleDistributeCosts = () => {
        if (!invoice.lines.length) {
            toast.info('Adicione linhas antes de distribuir custos');
            return;
        }

        const amountInput = window.prompt('Valor total a distribuir pelas linhas (EUR):', '0');
        if (amountInput === null) return;

        const totalToDistribute = Number(String(amountInput).replace(',', '.'));
        if (!Number.isFinite(totalToDistribute) || totalToDistribute <= 0) {
            toast.error('Valor inválido para distribuição');
            return;
        }

        const mode = window.prompt('Tipo de distribuição: "proporcional" ou "igual"', 'proporcional');
        if (mode === null) return;

        const normalizedMode = mode.trim().toLowerCase();
        const byValue = normalizedMode !== 'igual';

        const eligibleIndexes = invoice.lines
            .map((line, idx) => ({ idx, qty: Number(line.quantity) || 0, base: (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0) }))
            .filter(x => x.qty > 0)
            .map(x => x.idx);

        if (!eligibleIndexes.length) {
            toast.error('Não existem linhas com quantidade válida');
            return;
        }

        const lines = [...invoice.lines];
        const baseTotal = eligibleIndexes.reduce((sum, idx) => sum + ((Number(lines[idx].quantity) || 0) * (Number(lines[idx].unitPrice) || 0)), 0);

        let distributed = 0;
        eligibleIndexes.forEach((lineIndex, position) => {
            const line = lines[lineIndex];
            const qty = Number(line.quantity) || 1;

            let share = 0;
            if (position === eligibleIndexes.length - 1) {
                share = totalToDistribute - distributed;
            } else if (byValue && baseTotal > 0) {
                const lineBase = (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
                share = Number(((totalToDistribute * lineBase) / baseTotal).toFixed(6));
            } else {
                share = Number((totalToDistribute / eligibleIndexes.length).toFixed(6));
            }

            distributed += share;
            const deltaPerUnit = share / qty;
            line.unitPrice = Number((Number(line.unitPrice || 0) + deltaPerUnit).toFixed(4));

            const subtotal = (Number(line.quantity) || 0) * (Number(line.unitPrice) || 0);
            const discount = subtotal * ((Number(line.discountPercent) || 0) / 100);
            const afterDiscount = subtotal - discount;
            const tax = afterDiscount * ((Number(line.vatPercent) || 0) / 100);
            line.lineTotal = afterDiscount + tax;
        });

        setInvoice({ ...invoice, lines });
        calculateTotals(lines);
        toast.success(`Custos distribuídos (${formatCurrency(totalToDistribute)})`);
    };

    const calculateTotals = (lines) => {
        const subTotal = lines.reduce((sum, line) => sum + (line.quantity * line.unitPrice), 0);
        const lineDiscount = lines.reduce((sum, line) => sum + ((line.quantity * line.unitPrice) * (line.discountPercent / 100)), 0);
        const taxTotal = lines.reduce((sum, line) => {
            const afterDiscount = (line.quantity * line.unitPrice) - ((line.quantity * line.unitPrice) * (line.discountPercent / 100));
            return sum + (afterDiscount * (line.vatPercent / 100));
        }, 0);
        const total = lines.reduce((sum, line) => sum + line.lineTotal, 0);
        
        setInvoice(prev => ({ ...prev, subTotal, lineDiscount, taxTotal, total }));
    };

    const saveDocument = async ({ finalize = true } = {}) => {
        if (!invoice.customerId) {
            toast.error('Selecione um cliente');
            return;
        }
        
        try {
            setLoading(true);
            const documentTypeMap = {
                'Fatura': 'FT',
                'Fatura-Recibo': 'FR',
                'Nota de Encomenda': 'OR', // compatibilidade
                'Encomenda Cliente': 'OR',
                'Orçamento': 'ORC'
            };

            const trackedLines = [];
            for (const line of invoice.lines.filter(l => l.articleId)) {
                const normalized = {
                    ...line,
                    lotAllocations: [],
                    serialNumbers: []
                };

                let articleMeta = articles.find(a => a.id === Number(line.articleId));
                if (!articleMeta) {
                    try {
                        const articleRes = await api.get(`/billing/articles/${line.articleId}`);
                        articleMeta = articleRes.data;
                    } catch {
                        articleMeta = null;
                    }
                }

                const lotControl = !!(line.lotControl || articleMeta?.lotControl);
                const lotStrategy = line.lotStrategy || articleMeta?.lotStrategy || 'FIFO';
                const serialControl = !!(line.serialNumberControl || articleMeta?.serialNumberControl);

                const qty = Number(line.quantity || 0);
                if (serialControl && (!Number.isInteger(qty) || qty <= 0)) {
                    toast.error(`Artigo ${line.reference || line.description} com série exige quantidade inteira`);
                    return;
                }

                let availableLots = [];
                if (lotControl) {
                    const lotsRes = await api.get(`/billing/articles/${line.articleId}/lots`);
                    availableLots = (lotsRes.data || [])
                        .map(l => ({
                            ...l,
                            availableQty: Number(l.quantity || 0) - Number(l.reservedQuantity || 0)
                        }))
                        .filter(l => l.availableQty > 0);

                    if (!availableLots.length) {
                        toast.error(`Sem lotes disponíveis para ${line.reference || line.description}`);
                        return;
                    }
                }

                let availableSerials = [];
                if (serialControl) {
                    const serialRes = await api.get(`/billing/articles/${line.articleId}/serial-numbers`);
                    availableSerials = (serialRes.data || []).filter(s => s.status === 'Available');

                    if (!availableSerials.length) {
                        toast.error(`Sem números de série disponíveis para ${line.reference || line.description}`);
                        return;
                    }
                }

                if (lotControl || serialControl) {
                    const trackingSelection = await openTrackingModal({
                        lineName: line.reference || line.description,
                        requiredQty: qty,
                        lotStrategy,
                        lotEnabled: lotControl,
                        serialEnabled: serialControl,
                        lots: availableLots,
                        serials: availableSerials
                    });

                    if (!trackingSelection || trackingSelection.cancelled) {
                        setLoading(false);
                        return;
                    }

                    normalized.lotAllocations = Array.isArray(trackingSelection.lotAllocations) ? trackingSelection.lotAllocations : [];
                    normalized.serialNumbers = Array.isArray(trackingSelection.serialNumbers) ? trackingSelection.serialNumbers : [];
                }

                trackedLines.push(normalized);
            }

            const payload = {
                invoiceNumber: invoice.invoiceNumber || '',
                documentType: documentTypeMap[invoice.documentType] || invoice.documentType || 'FT',
                customerId: Number(invoice.customerId),
                warehouseId: invoice.warehouseId ? Number(invoice.warehouseId) : null,
                invoiceDate: invoice.invoiceDate,
                dueDate: invoice.dueDate || null,
                address: invoice.address || null,
                postalCode: invoice.postalCode || null,
                notes: invoice.notes || null,
                lines: trackedLines
                    .map(line => ({
                        articleId: Number(line.articleId),
                        description: line.description || '',
                        quantity: Number(line.quantity) || 0,
                        unitPrice: Number(line.unitPrice) || 0,
                        discountPercent: Number(line.discountPercent) || 0,
                        vatPercent: Number(line.vatPercent) || 0,
                    vatRateId: null,
                    lotAllocations: Array.isArray(line.lotAllocations) ? line.lotAllocations : [],
                    serialNumbers: Array.isArray(line.serialNumbers) ? line.serialNumbers : []
                    }))
            };

            let savedId = id ? Number(id) : null;

            if (isNew) {
                const createRes = await api.post('/billing/sales/invoices', payload);
                savedId = createRes?.data?.id || createRes?.data?.invoice?.id || null;
            } else {
                await api.put(`/billing/sales/invoices/${id}`, payload);
                savedId = Number(id);
            }

            if (finalize) {
                if (!savedId) {
                    throw new Error('Não foi possível identificar o documento para finalizar.');
                }
                await api.post(`/billing/sales/invoices/${savedId}/issue`);
                toast.success('Documento gravado e finalizado com sucesso!');
                setPrintOptions({
                    open: true,
                    context: {
                        kind: 'sales',
                        docId: savedId,
                        docType: payload.documentType || documentTypeMap[invoice.documentType] || 'FT',
                        title: 'Venda'
                    }
                });
                return;
            }

            toast.success('Documento gravado como suspenso.');
            if (isNew && savedId) {
                navigate(`/admin/sales/${savedId}`);
            }
        } catch (error) {
            toast.error('Erro ao gravar fatura');
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const openSuspendedDocuments = async () => {
        try {
            setLoadingSuspended(true);
            const res = await api.get('/billing/sales/invoices?page=1&pageSize=200&status=Draft');
            const docs = res?.data?.sales || [];
            setSuspendedDocs(docs);
            setShowSuspendedModal(true);
        } catch (error) {
            toast.error('Erro ao carregar documentos suspensos');
            console.error(error);
        } finally {
            setLoadingSuspended(false);
        }
    };

    const openSuspendedDocument = (docId) => {
        setShowSuspendedModal(false);
        navigate(`/admin/sales/${docId}`);
    };

    const loadArchiveFiles = async (saleId) => {
        try {
            setLoadingArchive(true);
            const res = await api.get(`/billing/sales/invoices/${saleId}/digital-archive`);
            setArchiveFiles(res?.data?.files || []);
        } catch (error) {
            toast.error('Erro ao carregar arquivo digital');
            console.error(error);
        } finally {
            setLoadingArchive(false);
        }
    };

    const openArchiveModal = async () => {
        const saleId = id;
        if (!saleId) {
            toast.info('Guarde o documento para ativar o arquivo digital');
            return;
        }
        setShowArchiveModal(true);
        await loadArchiveFiles(saleId);
    };

    const triggerArchiveUpload = () => {
        if (!id) {
            toast.info('Guarde o documento para ativar o arquivo digital');
            return;
        }
        archiveFileInputRef.current?.click();
    };

    const handleArchiveFileSelected = async (event) => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !id) return;

        try {
            setUploadingArchive(true);
            const formData = new FormData();
            formData.append('file', file);
            await api.post(`/billing/sales/invoices/${id}/digital-archive`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success('Ficheiro arquivado com sucesso');
            await loadArchiveFiles(id);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Erro ao arquivar ficheiro');
            console.error(error);
        } finally {
            setUploadingArchive(false);
        }
    };

    const removeArchiveFile = async (fileName) => {
        if (!id) return;
        if (!window.confirm('Remover este ficheiro do arquivo digital?')) return;

        try {
            await api.delete(`/billing/sales/invoices/${id}/digital-archive/${encodeURIComponent(fileName)}`);
            toast.success('Ficheiro removido');
            await loadArchiveFiles(id);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Erro ao remover ficheiro');
            console.error(error);
        }
    };

    const handleSendSms = async () => {
        if (!id) {
            toast.info('Guarde o documento antes de enviar SMS');
            return;
        }

        const suggested = `Documento ${invoice.invoiceNumber || `#${id}`} emitido no valor de ${Number(invoice.total || 0).toFixed(2)} EUR.`;
        const custom = window.prompt('Mensagem SMS (deixe em branco para usar o texto sugerido):', suggested);
        if (custom === null) return;

        try {
            setSendingSms(true);
            await api.post(`/billing/sales/invoices/${id}/send-sms`, {
                message: custom.trim() || suggested
            });
            toast.success('SMS enviado com sucesso');
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Falha no envio de SMS');
            console.error(error);
        } finally {
            setSendingSms(false);
        }
    };

    const handleCancel = async () => {
        if (!window.confirm('Tem certeza que deseja anular esta fatura?')) return;
        try {
            await api.delete(`/billing/sales/invoices/${id}`);
            toast.success('Fatura anulada com sucesso!');
            navigate('/admin/sales');
        } catch (error) {
            toast.error('Erro ao anular fatura');
        }
    };

    const handleDuplicate = () => {
        setInvoice({
            ...invoice,
            id: null,
            invoiceNumber: '',
            invoiceDate: new Date().toISOString().split('T')[0]
        });
        toast.info('Fatura duplicada. Altere os dados e grave.');
    };

    const handleConvertDocuments = async () => {
        try {
            const typePicked = window.prompt('Converter de que tipo? Escreva "encomenda", "orcamento" ou deixe vazio para ambos:', 'encomenda');
            if (typePicked === null) return;

            let sourceType = 'all';
            const normalized = typePicked.trim().toLowerCase();
            if (normalized.startsWith('enc')) sourceType = 'order';
            else if (normalized.startsWith('orc') || normalized.startsWith('orç')) sourceType = 'quote';

            const res = await api.get(`/billing/sales/convertible?sourceType=${sourceType}&page=1&pageSize=15`);
            const docs = res?.data?.documents || [];

            if (!docs.length) {
                toast.info('Sem documentos cliente elegíveis para conversão');
                return;
            }

            const hint = docs
                .map(d => `#${d.id} ${d.invoiceNumber} [${d.documentType}] - ${d.customerName || '-'} (${d.status})`)
                .join('\n');

            const picked = window.prompt(`Introduza o ID do documento de origem:\n\n${hint}`);
            if (picked === null) return;

            const sourceInvoiceId = Number(String(picked).trim());
            if (!Number.isInteger(sourceInvoiceId) || sourceInvoiceId <= 0) {
                toast.error('ID inválido');
                return;
            }

            const targetTypeInput = window.prompt('Tipo de documento de destino (FT ou FR):', 'FT');
            if (targetTypeInput === null) return;

            const targetDocumentType = targetTypeInput.trim().toUpperCase() || 'FT';
            if (!['FT', 'FR'].includes(targetDocumentType)) {
                toast.error('Tipo de destino inválido');
                return;
            }

            const convertRes = await api.post('/billing/sales/invoices/convert', {
                sourceInvoiceId,
                targetDocumentType
            });

            const newId = convertRes?.data?.invoiceId;
            if (!newId) {
                toast.error('Conversão sem retorno de documento');
                return;
            }

            toast.success('Documento convertido com sucesso');
            navigate(`/admin/sales/${newId}`);
        } catch (error) {
            toast.error(error?.response?.data?.message || 'Erro ao converter documento');
            console.error(error);
        }
    };

    const openTrackingModal = ({ lineName, requiredQty, lotStrategy, lotEnabled, serialEnabled, lots, serials }) => {
        return new Promise((resolve) => {
            trackingResolverRef.current = resolve;
            setTrackingModal({
                open: true,
                lineName,
                requiredQty,
                lotStrategy,
                lotEnabled,
                serialEnabled,
                lots: (lots || []).map(l => ({
                    id: l.id,
                    lotNumber: l.lotNumber,
                    expiryDate: l.expiryDate,
                    availableQty: Number(l.availableQty || 0),
                    quantity: 0
                })),
                serials: (serials || []).map(s => ({
                    id: s.id || s.serialNumber,
                    serialNumber: s.serialNumber,
                    lotNumber: s.lotNumber || '',
                    selected: false
                }))
            });
        });
    };

    const closeTrackingModal = (result) => {
        if (trackingResolverRef.current) {
            trackingResolverRef.current(result);
            trackingResolverRef.current = null;
        }
        setTrackingModal(prev => ({ ...prev, open: false }));
    };

    const updateLotAllocationQty = (lotId, rawValue) => {
        const qty = Math.max(0, Number(rawValue) || 0);
        setTrackingModal(prev => ({
            ...prev,
            lots: prev.lots.map(l => {
                if (l.id !== lotId) return l;
                const nextQty = Math.min(qty, l.availableQty);
                return { ...l, quantity: nextQty };
            })
        }));
    };

    const toggleSerialSelection = (serialId) => {
        setTrackingModal(prev => ({
            ...prev,
            serials: prev.serials.map(s => s.id === serialId ? { ...s, selected: !s.selected } : s)
        }));
    };

    const confirmTrackingModal = () => {
        const requiredQty = Number(trackingModal.requiredQty || 0);

        const selectedLots = trackingModal.lots
            .filter(l => Number(l.quantity || 0) > 0)
            .map(l => ({ lotId: Number(l.id), quantity: Number(l.quantity) }));

        const lotQty = selectedLots.reduce((sum, a) => sum + Number(a.quantity || 0), 0);
        if (trackingModal.lotEnabled && selectedLots.length > 0 && Math.abs(lotQty - requiredQty) > 0.0001) {
            toast.error(`A soma dos lotes tem de ser ${requiredQty}`);
            return;
        }

        const selectedSerials = trackingModal.serials
            .filter(s => s.selected)
            .map(s => s.serialNumber)
            .filter(Boolean);

        if (trackingModal.serialEnabled && selectedSerials.length > 0 && selectedSerials.length !== requiredQty) {
            toast.error(`Selecione exatamente ${requiredQty} série(s), ou nenhuma para automático`);
            return;
        }

        closeTrackingModal({
            cancelled: false,
            lotAllocations: selectedLots,
            serialNumbers: selectedSerials
        });
    };

    return (
        <div style={{ minHeight: '100vh', backgroundColor: 'var(--bg-card)' }}>
            <DocumentPrintOptionsModal
                open={!!printOptions.open}
                isDarkMode={localStorage.getItem('isDarkMode') === 'true' || window.matchMedia('(prefers-color-scheme: dark)').matches}
                context={printOptions.context}
                onClose={() => setPrintOptions({ open: false, context: null })}
                onDone={() => navigate('/admin/sales')}
            />
            {/* Ribbon Menu - Estilo Office */}
            <div style={{
                backgroundColor: 'var(--info-bg)',
                borderBottom: '3px solid var(--border-color)',
                padding: '0.5rem 1rem',
                display: 'flex',
                gap: '1rem',
                overflowX: 'auto',
                position: 'sticky',
                top: 0,
                zIndex: 100,
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}>
                {/* Opções */}
                <div style={ribbonGroupStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button onClick={() => navigate('/admin/sales')} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>🚫</div>
                            <div style={ribbonLabelStyle}>Cancelar<br/>documento</div>
                        </button>
                        <button onClick={() => saveDocument({ finalize: true })} disabled={loading} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>💾</div>
                            <div style={ribbonLabelStyle}>Gravar<br/>documento</div>
                        </button>
                        <button onClick={handleCancel} disabled={isNew} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>❌</div>
                            <div style={ribbonLabelStyle}>Anular<br/>documento</div>
                        </button>
                        <button onClick={handleDuplicate} disabled={isNew} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>📑</div>
                            <div style={ribbonLabelStyle}>Duplicar</div>
                        </button>
                        <button onClick={handleDistributeCosts} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>💰</div>
                            <div style={ribbonLabelStyle}>Distribuir<br/>Custos</div>
                        </button>
                    </div>
                    <div style={ribbonGroupLabelStyle}>Opções</div>
                </div>

                <div style={ribbonSeparatorStyle}></div>

                {/* Documentos Suspensos */}
                <div style={ribbonGroupStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button onClick={() => saveDocument({ finalize: false })} disabled={loading} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>📋</div>
                            <div style={ribbonLabelStyle}>Gravar Como<br/>Suspenso</div>
                        </button>
                        <button onClick={openSuspendedDocuments} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>📂</div>
                            <div style={ribbonLabelStyle}>Abrir<br/>Suspenso</div>
                        </button>
                    </div>
                    <div style={ribbonGroupLabelStyle}>Documentos Suspensos</div>
                </div>

                <div style={ribbonSeparatorStyle}></div>

                {/* Impressão */}
                <div style={ribbonGroupStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button onClick={() => window.print()} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>🖨️</div>
                            <div style={ribbonLabelStyle}>Imprimir<br/>(F4)</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>👁️</div>
                            <div style={ribbonLabelStyle}>Ver antes<br/>Layout</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>✏️</div>
                            <div style={ribbonLabelStyle}>Editar<br/>email</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>📧</div>
                            <div style={ribbonLabelStyle}>Enviar<br/>email</div>
                        </button>
                        <button onClick={handleSendSms} disabled={sendingSms} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>📱</div>
                            <div style={ribbonLabelStyle}>{sendingSms ? 'A enviar...' : <>Enviar<br/>SMS</>}</div>
                        </button>
                    </div>
                    <div style={ribbonGroupLabelStyle}>Impressão</div>
                </div>

                <div style={ribbonSeparatorStyle}></div>

                {/* Conversão */}
                <div style={ribbonGroupStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>🔗</div>
                            <div style={ribbonLabelStyle}>Referenciar<br/>documentos</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>↩️</div>
                            <div style={ribbonLabelStyle}>Devolução</div>
                        </button>
                        <button onClick={handleConvertDocuments} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>🔄</div>
                            <div style={ribbonLabelStyle}>Converter<br/>documentos</div>
                        </button>
                    </div>
                    <div style={ribbonGroupLabelStyle}>Conversão</div>
                </div>

                <div style={ribbonSeparatorStyle}></div>

                {/* Unitários */}
                <div style={ribbonGroupStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>📋</div>
                            <div style={ribbonLabelStyle}>Copiar para<br/>linhas</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>🤖</div>
                            <div style={ribbonLabelStyle}>Assistente para<br/>inserir artigos</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>📥</div>
                            <div style={ribbonLabelStyle}>Importar</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>📊</div>
                            <div style={ribbonLabelStyle}>Importador</div>
                        </button>
                    </div>
                    <div style={ribbonGroupLabelStyle}>Unitários</div>
                </div>

                <div style={ribbonSeparatorStyle}></div>

                {/* Configurações */}
                <div style={ribbonGroupStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>⚠️</div>
                            <div style={ribbonLabelStyle}>Avisos<br/>dinâmicos</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>📑</div>
                            <div style={ribbonLabelStyle}>Ordem dos<br/>campos</div>
                        </button>
                        <button type="button" style={ribbonButtonStyle} disabled title="Ainda não disponível nesta versão">
                            <div style={ribbonIconStyle}>⚙️</div>
                            <div style={ribbonLabelStyle}>Configurar<br/>áreas</div>
                        </button>
                    </div>
                    <div style={ribbonGroupLabelStyle}>Configurações</div>
                </div>

                <div style={ribbonSeparatorStyle}></div>

                {/* Arquivo Digital */}
                <div style={ribbonGroupStyle}>
                    <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button onClick={triggerArchiveUpload} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>📁</div>
                            <div style={ribbonLabelStyle}>Arquivar</div>
                        </button>
                        <button onClick={openArchiveModal} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>💾</div>
                            <div style={ribbonLabelStyle}>Arquivo<br/>Digital</div>
                        </button>
                        <button onClick={() => navigate('/admin/sales')} style={ribbonButtonStyle}>
                            <div style={ribbonIconStyle}>🚪</div>
                            <div style={ribbonLabelStyle}>Sair</div>
                        </button>
                    </div>
                    <div style={ribbonGroupLabelStyle}>Arquivo Digital</div>
                </div>
            </div>

            {/* Header */}
            <div style={{ padding: '1rem 2rem', backgroundColor: 'white', borderBottom: '1px solid var(--border-color)' }}>
                <h1 style={{ margin: 0, fontSize: '1.5rem', color: 'var(--text-primary)' }}>
                    {isNew ? '📝 Nova Venda' : `📄 ${invoice.documentType} ${invoice.invoiceNumber}`}
                </h1>
            </div>

            {/* Form */}
            <div style={{ padding: '2rem' }}>
                <div style={{ backgroundColor: 'white', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
                    {/* Header Grid - First Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label style={labelStyle}>Documento:</label>
                            <select value={invoice.documentType} onChange={(e) => setInvoice({...invoice, documentType: e.target.value})} style={inputStyle}>
                                <option>Encomenda Cliente</option>
                                <option>Orçamento</option>
                                <option>Fatura</option>
                                <option>Fatura-Recibo</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Série:</label>
                            <input type="text" value={invoice.series} onChange={(e) => setInvoice({...invoice, series: e.target.value})} style={inputStyle} />
                        </div>
                        <div>
                            <label style={labelStyle}>Nº:</label>
                            <input type="text" value={invoice.invoiceNumber} placeholder={isNew ? "Auto" : ""} readOnly={isNew} style={{...inputStyle, fontWeight: '600'}} />
                        </div>
                        <div>
                            <label style={labelStyle}>Data:</label>
                            <input type="date" value={invoice.invoiceDate} onChange={(e) => setInvoice({...invoice, invoiceDate: e.target.value})} style={inputStyle} />
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                            <label style={labelStyle}>Cliente: *</label>
                            <div style={{ display: 'flex', gap: '0.25rem' }}>
                                <input 
                                    type="text" 
                                    placeholder="Pesquisar" 
                                    value={invoice.customerName || invoice.customerCode} 
                                    onChange={(e) => setCustomerSearchTerm(e.target.value)} 
                                    style={{...inputStyle, flex: 1}} 
                                    readOnly
                                />
                                <button 
                                    onClick={searchCustomer} 
                                    style={{ 
                                        padding: '0.5rem 0.75rem', 
                                        background: 'var(--info)', 
                                        color: 'white', 
                                        border: 'none', 
                                        borderRadius: '4px', 
                                        cursor: 'pointer' 
                                    }}
                                >
                                    🔍
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Second Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                        <div>
                            <label style={labelStyle}>Vendedor:</label>
                            <input type="text" value="1" readOnly style={{...inputStyle, backgroundColor: 'var(--bg-card)'}} />
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                            <label style={labelStyle}>Zona Fiscal:</label>
                            <select value={invoice.taxZone} onChange={(e) => setInvoice({...invoice, taxZone: e.target.value})} style={inputStyle}>
                                <option>Continente Portugal</option>
                                <option>Açores</option>
                                <option>Madeira</option>
                            </select>
                        </div>
                        <div style={{ gridColumn: 'span 3' }}>
                            <label style={labelStyle}>Nome:</label>
                            <input type="text" value={invoice.customerName} readOnly style={{...inputStyle, backgroundColor: 'var(--bg-card)'}} />
                        </div>
                    </div>

                    {/* Third Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '1rem', marginBottom: '1rem' }}>
                        <div style={{ gridColumn: 'span 2' }}>
                            <label style={labelStyle}>Prazo Pagamento:</label>
                            <select value={invoice.paymentTerms} onChange={(e) => setInvoice({...invoice, paymentTerms: e.target.value})} style={inputStyle}>
                                <option>Não usar</option>
                                <option>30 dias</option>
                                <option>60 dias</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Dt. Venc.:</label>
                            <input type="date" value={invoice.dueDate} onChange={(e) => setInvoice({...invoice, dueDate: e.target.value})} style={inputStyle} />
                        </div>
                        <div style={{ gridColumn: 'span 3' }}>
                            <label style={labelStyle}>Morada:</label>
                            <input type="text" value={invoice.address} readOnly style={{...inputStyle, backgroundColor: 'var(--bg-card)'}} />
                        </div>
                    </div>

                    {/* Fourth Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                        <div style={{ gridColumn: 'span 2' }}>
                            <label style={labelStyle}>Tipo Pagamento:</label>
                            <select value={invoice.paymentMethod} onChange={(e) => setInvoice({...invoice, paymentMethod: e.target.value})} style={inputStyle}>
                                <option>Nenhum</option>
                                <option>Dinheiro</option>
                                <option>Multibanco</option>
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>V/ Ref.:</label>
                            <input type="text" value={invoice.reference} onChange={(e) => setInvoice({...invoice, reference: e.target.value})} style={inputStyle} />
                        </div>
                        <div style={{ gridColumn: 'span 2' }}>
                            <label style={labelStyle}>Cod. Postal:</label>
                            <input type="text" value={invoice.postalCode} readOnly style={{...inputStyle, backgroundColor: 'var(--bg-card)'}} />
                        </div>
                        <div>
                            <label style={labelStyle}>Contribuinte:</label>
                            <input type="text" value={invoice.taxNumber} readOnly style={{...inputStyle, backgroundColor: 'var(--bg-card)'}} />
                        </div>
                    </div>

                    {/* Fifth Row */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: '1rem', marginBottom: '1.5rem' }}>
                        <div style={{ gridColumn: 'span 2' }}>
                            <label style={labelStyle}>Armazém:</label>
                            <select value={invoice.warehouseId || ''} onChange={(e) => setInvoice({...invoice, warehouseId: e.target.value ? Number(e.target.value) : null})} style={inputStyle}>
                                <option value="">Sem armazém</option>
                                {warehouses.map(w => (
                                    <option key={w.id} value={w.id}>{w.code ? `[${w.code}] ${w.name}` : w.name}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label style={labelStyle}>Dt. Stock:</label>
                            <input type="datetime-local" value={invoice.stockDate?.substring(0,16)} onChange={(e) => setInvoice({...invoice, stockDate: e.target.value})} style={inputStyle} />
                        </div>
                        <div style={{ gridColumn: 'span 3', display: 'flex', alignItems: 'flex-end' }}>
                            <div style={{ flex: 1 }}>
                                <label style={labelStyle}>Saldo:</label>
                                <input type="text" value={formatCurrency(invoice.balance)} readOnly style={{...inputStyle, backgroundColor: 'var(--bg-card)', textAlign: 'right', fontWeight: '600'}} />
                            </div>
                        </div>
                    </div>

                    {/* Lines Table */}
                    <div style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid var(--border-color)', borderRadius: '8px', backgroundColor: 'var(--bg-card)' }}>
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white' }}>
                                <thead>
                                    <tr style={{ borderBottom: '2px solid var(--border-color)', background: 'var(--info-bg)' }}>
                                        <th style={{...tableHeaderStyle, width: '90px'}}>Referência</th>
                                        <th style={{...tableHeaderStyle, width: '380px'}}>Descrição</th>
                                        <th style={{...tableHeaderStyle, width: '80px'}}>Quant.</th>
                                        <th style={{...tableHeaderStyle, width: '100px'}}>Qnt. Disponível</th>
                                        <th style={{...tableHeaderStyle, width: '100px'}}>Preço</th>
                                        <th style={{...tableHeaderStyle, width: '80px'}}>Desc.</th>
                                        <th style={{...tableHeaderStyle, width: '100px'}}>Imposto</th>
                                        <th style={{...tableHeaderStyle, width: '100px'}}>Total</th>
                                        <th style={{ padding: '10px', width: '50px' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {invoice.lines.map((line, i) => (
                                        <tr key={i} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                            <td style={{ padding: '4px 8px' }}>
                                                <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                                                    <input 
                                                        type="text" 
                                                        value={line.reference || ''} 
                                                        onChange={(e) => updateLine(i, 'reference', e.target.value)} 
                                                        onKeyDown={(e) => handleLineKeyDown(e, i, 'reference')}
                                                        placeholder="Código" 
                                                        ref={setCellRef(i, 'reference')}
                                                        style={{...tableCellInputStyle, flex: 1, minWidth: '40px'}} 
                                                    />
                                                    <button 
                                                        onClick={() => searchArticles(i)} 
                                                        style={{ 
                                                            padding: '4px 8px', 
                                                            background: 'var(--bg-tertiary)', 
                                                            border: '1px solid var(--border-color)', 
                                                            borderRadius: '4px', 
                                                            cursor: 'pointer',
                                                            fontSize: '0.75rem'
                                                        }}
                                                    >
                                                        🔍
                                                    </button>
                                                </div>
                                            </td>
                                            <td style={{ padding: '4px 8px' }}>
                                                <input 
                                                    type="text" 
                                                    value={line.description || ''} 
                                                    onChange={(e) => updateLine(i, 'description', e.target.value)} 
                                                    onKeyDown={(e) => handleLineKeyDown(e, i, 'description')}
                                                    placeholder="Descrição" 
                                                    ref={setCellRef(i, 'description')}
                                                    style={{...tableCellInputStyle, width: '100%'}} 
                                                />
                                            </td>
                                            <td style={{ padding: '4px 8px' }}>
                                                <input 
                                                    type="number" 
                                                    step="0.01"
                                                    value={line.quantity} 
                                                    onChange={(e) => updateLine(i, 'quantity', parseFloat(e.target.value) || 0)} 
                                                    onKeyDown={(e) => handleLineKeyDown(e, i, 'quantity')}
                                                    ref={setCellRef(i, 'quantity')}
                                                    style={{...tableCellInputStyle, width: '100%', textAlign: 'right'}} 
                                                />
                                            </td>
                                            <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                                {line.availableQty.toFixed(2)}
                                            </td>
                                            <td style={{ padding: '4px 8px' }}>
                                                <input 
                                                    type="number" 
                                                    step="0.01"
                                                    value={line.unitPrice} 
                                                    onChange={(e) => updateLine(i, 'unitPrice', parseFloat(e.target.value) || 0)} 
                                                    onKeyDown={(e) => handleLineKeyDown(e, i, 'unitPrice')}
                                                    ref={setCellRef(i, 'unitPrice')}
                                                    style={{...tableCellInputStyle, width: '100%', textAlign: 'right'}} 
                                                />
                                            </td>
                                            <td style={{ padding: '4px 8px' }}>
                                                <input 
                                                    type="number" 
                                                    step="0.01"
                                                    value={line.discountPercent} 
                                                    onChange={(e) => updateLine(i, 'discountPercent', parseFloat(e.target.value) || 0)} 
                                                    onKeyDown={(e) => handleLineKeyDown(e, i, 'discountPercent')}
                                                    ref={setCellRef(i, 'discountPercent')}
                                                    style={{...tableCellInputStyle, width: '100%', textAlign: 'right'}} 
                                                />
                                            </td>
                                            <td style={{ padding: '4px 8px', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                                                {line.vatPercent > 0 ? `${line.vatPercent.toFixed(2)}%` : 'Nenhum'}
                                            </td>
                                            <td style={{ padding: '4px 8px', textAlign: 'right', fontSize: '0.875rem', fontWeight: '600' }}>
                                                {formatCurrency(line.lineTotal)}
                                            </td>
                                            <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                                                <button 
                                                    onClick={() => removeLine(i)} 
                                                    style={{ 
                                                        background: 'var(--error)', 
                                                        color: 'white', 
                                                        border: 'none', 
                                                        borderRadius: '4px', 
                                                        padding: '4px 8px', 
                                                        cursor: 'pointer', 
                                                        fontSize: '0.75rem' 
                                                    }}
                                                >
                                                    🗑️
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    {invoice.lines.length === 0 && (
                                        <tr>
                                            <td colSpan={9} style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)', fontSize: '0.875rem' }}>
                                                Nenhuma linha adicionada. Clique em "+ Linha" para adicionar artigos.
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        <button 
                            onClick={addLine} 
                            style={{ 
                                marginTop: '1rem', 
                                padding: '0.5rem 1rem', 
                                background: 'var(--success)', 
                                color: '#fff', 
                                border: 'none', 
                                borderRadius: '4px', 
                                cursor: 'pointer', 
                                fontSize: '0.875rem', 
                                fontWeight: '600' 
                            }}
                        >
                            + Linha
                        </button>
                    </div>

                    {/* Totals */}
                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <div style={{ minWidth: '300px', padding: '1rem', backgroundColor: 'var(--info-bg)', borderRadius: '8px' }}>
                            <div style={totalRowStyle}>
                                <span>Total Bruto:</span>
                                <span style={{ fontWeight: '600' }}>{formatCurrency(invoice.subTotal)}</span>
                            </div>
                            <div style={totalRowStyle}>
                                <span>Desconto de Linha:</span>
                                <span style={{ fontWeight: '600', color: 'var(--error)' }}>{formatCurrency(invoice.lineDiscount)}</span>
                            </div>
                            <div style={totalRowStyle}>
                                <span>Desconto Global %:</span>
                                <span style={{ fontWeight: '600' }}>0</span>
                            </div>
                            <div style={totalRowStyle}>
                                <span>Desconto Global €:</span>
                                <span style={{ fontWeight: '600' }}>{formatCurrency(0)}</span>
                            </div>
                            <div style={totalRowStyle}>
                                <span>Total Líquido:</span>
                                <span style={{ fontWeight: '600' }}>{formatCurrency(invoice.subTotal - invoice.lineDiscount)}</span>
                            </div>
                            <div style={totalRowStyle}>
                                <span>Total Impostos:</span>
                                <span style={{ fontWeight: '600' }}>{formatCurrency(invoice.taxTotal)}</span>
                            </div>
                            <div style={{...totalRowStyle, borderTop: '2px solid var(--border-color)', paddingTop: '0.75rem', marginTop: '0.5rem', fontSize: '1.1rem', fontWeight: '700'}}>
                                <span>Total Documento:</span>
                                <span style={{ color: 'var(--success)' }}>{formatCurrency(invoice.total)}</span>
                            </div>
                            <div style={totalRowStyle}>
                                <span>Total Retenção:</span>
                                <span style={{ fontWeight: '600' }}>0</span>
                            </div>
                            <div style={{...totalRowStyle, backgroundColor: 'var(--success-bg)', padding: '0.75rem', borderRadius: '6px', fontSize: '1.2rem', fontWeight: '700'}}>
                                <span>Valor a Pagar:</span>
                                <span style={{ color: 'var(--success)' }}>{formatCurrency(invoice.total)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {showSuspendedModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        width: '90%',
                        maxWidth: '900px',
                        maxHeight: '80vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{
                            padding: '1.5rem',
                            borderBottom: '1px solid var(--border-color)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>📋 Documentos Suspensos (Draft)</h2>
                            <button
                                onClick={() => setShowSuspendedModal(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary)'
                                }}
                            >
                                ×
                            </button>
                        </div>
                        <div style={{ flex: 1, overflow: 'auto', padding: '0 1rem 1rem' }}>
                            {loadingSuspended ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>A carregar...</div>
                            ) : suspendedDocs.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Sem documentos suspensos</div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Nº</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Cliente</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Data</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Total</th>
                                            <th style={{ padding: '0.75rem', width: '120px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {suspendedDocs.map(doc => (
                                            <tr key={doc.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                <td style={{ padding: '0.75rem' }}>{doc.invoiceNumber || `#${doc.id}`}</td>
                                                <td style={{ padding: '0.75rem' }}>{doc.customer?.name || '-'}</td>
                                                <td style={{ padding: '0.75rem' }}>{doc.issueDate ? String(doc.issueDate).slice(0, 10) : '-'}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'right', fontWeight: '600' }}>{formatCurrency(doc.total)}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => openSuspendedDocument(doc.id)}
                                                        style={{
                                                            padding: '0.45rem 0.75rem',
                                                            background: 'var(--success)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.875rem'
                                                        }}
                                                    >
                                                        Abrir
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {showArchiveModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        width: '90%',
                        maxWidth: '900px',
                        maxHeight: '80vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{
                            padding: '1.5rem',
                            borderBottom: '1px solid var(--border-color)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>💾 Arquivo Digital - Venda</h2>
                            <button
                                onClick={() => setShowArchiveModal(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary)'
                                }}
                            >
                                ×
                            </button>
                        </div>

                        <div style={{ padding: '1rem', borderBottom: '1px solid var(--border-color)', display: 'flex', gap: '0.75rem' }}>
                            <button
                                onClick={triggerArchiveUpload}
                                disabled={uploadingArchive}
                                style={{
                                    padding: '0.55rem 0.9rem',
                                    background: 'var(--info)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem'
                                }}
                            >
                                {uploadingArchive ? 'A arquivar...' : '📁 Arquivar ficheiro'}
                            </button>
                            <button
                                onClick={() => id && loadArchiveFiles(id)}
                                disabled={loadingArchive}
                                style={{
                                    padding: '0.55rem 0.9rem',
                                    background: 'var(--info-bg)',
                                    color: 'var(--text-primary)',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '0.875rem'
                                }}
                            >
                                Atualizar
                            </button>
                        </div>

                        <div style={{ flex: 1, overflow: 'auto', padding: '0 1rem 1rem' }}>
                            {loadingArchive ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>A carregar...</div>
                            ) : archiveFiles.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>Sem ficheiros arquivados</div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Ficheiro</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'right' }}>Tamanho</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left' }}>Data</th>
                                            <th style={{ padding: '0.75rem', width: '180px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {archiveFiles.map(file => (
                                            <tr key={file.fileName} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                <td style={{ padding: '0.75rem' }}>{file.originalFileName || file.fileName}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'right' }}>{((Number(file.size) || 0) / 1024).toFixed(1)} KB</td>
                                                <td style={{ padding: '0.75rem' }}>{file.uploadedAt ? new Date(file.uploadedAt).toLocaleString('pt-PT') : '-'}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => window.open(file.url, '_blank')}
                                                        style={{
                                                            padding: '0.45rem 0.65rem',
                                                            background: 'var(--success)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.8rem',
                                                            marginRight: '0.4rem'
                                                        }}
                                                    >
                                                        Abrir
                                                    </button>
                                                    <button
                                                        onClick={() => removeArchiveFile(file.fileName)}
                                                        style={{
                                                            padding: '0.45rem 0.65rem',
                                                            background: 'var(--error)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.8rem'
                                                        }}
                                                    >
                                                        Remover
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Customer Search Modal */}
            {showCustomerSearch && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        width: '90%',
                        maxWidth: '800px',
                        maxHeight: '80vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{
                            padding: '1.5rem',
                            borderBottom: '1px solid var(--border-color)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>👥 Pesquisar Cliente</h2>
                            <button
                                onClick={() => setShowCustomerSearch(false)}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary)'
                                }}
                            >
                                \u00D7
                            </button>
                        </div>
                        <div style={{ padding: '1rem' }}>
                            <input
                                type="text"
                                placeholder="Pesquisar por nome, email ou código..."
                                value={customerSearchTerm}
                                onChange={(e) => setCustomerSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && searchCustomer()}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '4px',
                                    fontSize: '1rem'
                                }}
                                autoFocus
                            />
                        </div>
                        <div style={{ flex: 1, overflow: 'auto', padding: '0 1rem 1rem' }}>
                            {customers.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                                    Digite para pesquisar clientes
                                </div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>Código</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>Nome</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>Email</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>NIF</th>
                                            <th style={{ padding: '0.75rem', width: '100px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {customers.map(customer => (
                                            <tr key={customer.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                <td style={{ padding: '0.75rem', fontSize: '0.95rem', fontWeight: '700', color: 'var(--info-text)' }}>{getCustomerCode(customer)}</td>
                                                <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{customer.name}</td>
                                                <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{customer.email}</td>
                                                <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{customer.nif || customer.taxNumber || '-'}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => selectCustomer(customer)}
                                                        style={{
                                                            padding: '0.5rem 1rem',
                                                            background: 'var(--success)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.875rem'
                                                        }}
                                                    >
                                                        Selecionar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            <input
                ref={archiveFileInputRef}
                type="file"
                onChange={handleArchiveFileSelected}
                style={{ display: 'none' }}
            />

            {/* Article Search Modal */}
            {showArticleSearch && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0,0,0,0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        borderRadius: '8px',
                        width: '90%',
                        maxWidth: '1000px',
                        maxHeight: '80vh',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}>
                        <div style={{
                            padding: '1.5rem',
                            borderBottom: '1px solid var(--border-color)',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <h2 style={{ margin: 0, fontSize: '1.25rem' }}>📦 Pesquisar Artigo</h2>
                            <button
                                onClick={() => { setShowArticleSearch(false); setSelectedLineIndex(null); }}
                                style={{
                                    background: 'transparent',
                                    border: 'none',
                                    fontSize: '1.5rem',
                                    cursor: 'pointer',
                                    color: 'var(--text-secondary)'
                                }}
                            >
                                \u00D7
                            </button>
                        </div>
                        <div style={{ padding: '1rem' }}>
                            <input
                                type="text"
                                placeholder="Pesquisar por código, descrição..."
                                value={articleSearchTerm}
                                onChange={(e) => setArticleSearchTerm(e.target.value)}
                                onKeyPress={(e) => e.key === 'Enter' && searchArticles(selectedLineIndex)}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem',
                                    border: '1px solid var(--border-color)',
                                    borderRadius: '4px',
                                    fontSize: '1rem'
                                }}
                                autoFocus
                            />
                        </div>
                        <div style={{ flex: 1, overflow: 'auto', padding: '0 1rem 1rem' }}>
                            {articles.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-secondary)' }}>
                                    Digite para pesquisar artigos
                                </div>
                            ) : (
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr style={{ borderBottom: '2px solid var(--border-color)' }}>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>Código</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'left', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>Descrição</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>Preço</th>
                                            <th style={{ padding: '0.75rem', textAlign: 'right', fontSize: '0.75rem', fontWeight: '700', color: 'var(--text-secondary)' }}>Stock</th>
                                            <th style={{ padding: '0.75rem', width: '100px' }}></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {articles.map(article => (
                                            <tr key={article.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                                                <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{article.reference}</td>
                                                <td style={{ padding: '0.75rem', fontSize: '0.875rem' }}>{article.description}</td>
                                                <td style={{ padding: '0.75rem', fontSize: '0.875rem', textAlign: 'right' }}>{formatCurrency(article.salePriceWithVat || article.salePriceNoVat || 0)}</td>
                                                <td style={{ padding: '0.75rem', fontSize: '0.875rem', textAlign: 'right' }}>{article.stock || 0}</td>
                                                <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                                                    <button
                                                        onClick={() => selectArticle(article)}
                                                        style={{
                                                            padding: '0.5rem 1rem',
                                                            background: 'var(--success)',
                                                            color: 'white',
                                                            border: 'none',
                                                            borderRadius: '4px',
                                                            cursor: 'pointer',
                                                            fontSize: '0.875rem'
                                                        }}
                                                    >
                                                        Selecionar
                                                    </button>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {trackingModal.open && (
                <div style={trackingBackdropStyle}>
                    <div style={trackingModalStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                            <h3 style={{ margin: 0, fontSize: '1.1rem' }}>Selecionar lote/série</h3>
                            <button
                                onClick={() => closeTrackingModal({ cancelled: true })}
                                style={{ border: 'none', background: 'transparent', fontSize: '1.25rem', cursor: 'pointer' }}
                            >
                                ✕
                            </button>
                        </div>

                        <div style={{ marginBottom: '0.75rem', color: 'var(--text-primary)', fontSize: '0.9rem' }}>
                            <strong>Artigo:</strong> {trackingModal.lineName} | <strong>Qtd:</strong> {trackingModal.requiredQty}
                        </div>

                        {trackingModal.lotEnabled && (
                            <div style={{ marginBottom: '1rem' }}>
                                <div style={{ marginBottom: '0.4rem', fontWeight: 700, fontSize: '0.9rem' }}>
                                    Lotes disponíveis (estratégia automática: {trackingModal.lotStrategy})
                                </div>
                                <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px' }}>
                                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                        <thead>
                                            <tr style={{ background: 'var(--info-bg)' }}>
                                                <th style={trackingThStyle}>Lote</th>
                                                <th style={trackingThStyle}>Validade</th>
                                                <th style={trackingThStyle}>Disponível</th>
                                                <th style={trackingThStyle}>Usar</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {trackingModal.lots.map(lot => (
                                                <tr key={lot.id} style={{ borderTop: '1px solid var(--border-color)' }}>
                                                    <td style={trackingTdStyle}>{lot.lotNumber || '-'}</td>
                                                    <td style={trackingTdStyle}>{lot.expiryDate ? String(lot.expiryDate).slice(0, 10) : '-'}</td>
                                                    <td style={trackingTdStyle}>{Number(lot.availableQty).toFixed(2)}</td>
                                                    <td style={trackingTdStyle}>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            max={lot.availableQty}
                                                            step="0.01"
                                                            value={lot.quantity}
                                                            onChange={(e) => updateLotAllocationQty(lot.id, e.target.value)}
                                                            style={{ width: '90px', padding: '0.3rem' }}
                                                        />
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                                <div style={{ marginTop: '0.4rem', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>
                                    Dica: deixe tudo a zero para usar alocação automática por {trackingModal.lotStrategy}.
                                </div>
                            </div>
                        )}

                        {trackingModal.serialEnabled && (
                            <div style={{ marginBottom: '1rem' }}>
                                <div style={{ marginBottom: '0.4rem', fontWeight: 700, fontSize: '0.9rem' }}>
                                    Séries disponíveis (selecione {trackingModal.requiredQty} ou deixe vazio para automático)
                                </div>
                                <div style={{ maxHeight: '180px', overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: '6px', padding: '0.5rem' }}>
                                    {trackingModal.serials.map(s => (
                                        <label key={s.id} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.25rem 0', borderBottom: '1px dashed var(--border-color)' }}>
                                            <input
                                                type="checkbox"
                                                checked={!!s.selected}
                                                onChange={() => toggleSerialSelection(s.id)}
                                            />
                                            <span style={{ fontSize: '0.9rem' }}>{s.serialNumber}</span>
                                            {s.lotNumber && <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>(Lote: {s.lotNumber})</span>}
                                        </label>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                            <button
                                onClick={() => closeTrackingModal({ cancelled: true })}
                                style={{ padding: '0.55rem 1rem', border: '1px solid var(--border-color)', background: 'var(--bg-card)', borderRadius: '6px', cursor: 'pointer' }}
                            >
                                Cancelar
                            </button>
                            <button
                                onClick={confirmTrackingModal}
                                style={{ padding: '0.55rem 1rem', border: 'none', background: 'var(--info)', color: '#fff', borderRadius: '6px', cursor: 'pointer' }}
                            >
                                Confirmar seleção
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Styles - Ribbon Menu
const ribbonButtonStyle = {
    padding: '0.5rem',
    background: 'transparent',
    border: '1px solid transparent',
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '70px',
    transition: 'all 0.2s',
    ':hover': {
        background: 'var(--bg-tertiary)',
        borderColor: 'var(--border-color)'
    }
};

const ribbonIconStyle = {
    fontSize: '1.5rem',
    marginBottom: '0.25rem'
};

const ribbonLabelStyle = {
    fontSize: '0.65rem',
    textAlign: 'center',
    lineHeight: '1.1',
    color: 'var(--text-primary)'
};

const ribbonGroupStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem'
};

const ribbonGroupLabelStyle = {
    fontSize: '0.7rem',
    color: 'var(--text-secondary)',
    textAlign: 'center',
    paddingTop: '0.25rem',
    borderTop: '1px solid var(--border-color)'
};

const ribbonSeparatorStyle = {
    width: '1px',
    background: 'var(--bg-tertiary)',
    margin: '0.25rem 0'
};

const labelStyle = {
    fontSize: '0.7rem',
    color: 'var(--text-secondary)',
    fontWeight: '700',
    display: 'block',
    marginBottom: '0.25rem',
    textTransform: 'uppercase'
};

const inputStyle = {
    width: '100%',
    padding: '0.5rem',
    border: '1px solid var(--border-color)',
    borderRadius: '4px',
    fontSize: '0.875rem'
};

const tableHeaderStyle = {
    padding: '10px',
    textAlign: 'left',
    fontSize: '0.75rem',
    fontWeight: '700',
    color: 'var(--text-secondary)',
    textTransform: 'uppercase'
};

const tableCellInputStyle = {
    width: '100%',
    padding: '0.25rem',
    border: '1px solid var(--border-color)',
    borderRadius: '3px',
    fontSize: '0.875rem'
};

const totalRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '0.5rem 0',
    fontSize: '0.875rem'
};

const trackingBackdropStyle = {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1200,
    padding: '1rem'
};

const trackingModalStyle = {
    width: 'min(860px, 96vw)',
    maxHeight: '90vh',
    overflowY: 'auto',
    background: 'var(--bg-card)',
    borderRadius: '10px',
    padding: '1rem',
    boxShadow: '0 12px 32px rgba(0,0,0,0.24)'
};

const trackingThStyle = {
    textAlign: 'left',
    padding: '0.45rem 0.55rem',
    fontSize: '0.78rem',
    color: 'var(--text-primary)',
    textTransform: 'uppercase'
};

const trackingTdStyle = {
    padding: '0.45rem 0.55rem',
    fontSize: '0.88rem'
};

export default SaleInvoiceForm;
